import posthog, { getCookieConsent } from '../posthog';

// TYPES

type FailedProperties = { error?: string; error_type?: string };

// Auth & Onboarding
type AuthProperties = { method?: 'email' | 'google' };
type OnboardingStepProperties = { step_name: string; step_index?: number };

// Transactions
type TransactionCreatedProperties = {
    transaction_type: 'income' | 'expense';
    category: string;
    source: 'manual' | 'import' | 'recurring';
    has_account: boolean;
};

type TransactionUpdatedProperties = {
    transaction_type?: 'income' | 'expense';
    category?: string;
    changes?: Record<string, unknown>;
};

type TransactionDeletedProperties = {
    transaction_id: string;
    reason?: string;
};

type TransactionCreationFailedProperties = {
    transaction_type?: 'income' | 'expense';
    category?: string;
};

// Budgets, goals & debts
type BudgetCreatedProperties = {
    category: string;
    source?: string;
};

type BudgetCreationFailedProperties = {
    category?: string;
    source?: string;
};

type SavingsGoalCreatedProperties = {
    has_target_date: boolean,
    has_target_amount: boolean,
    is_household_goal: boolean
};

type DebtPaymentRecordedProperties = {
    is_paid_off: boolean
};

// Imports and statement parsing
type StatementParsingStartedProperties = {
    operation_id?: string;
    bank_choice?: string;
};

type StatementParsedProperties = {
    operation_id?: string;
    transactions_count: number;
    confidence_score?: number;
    parse_time_ms?: number;
    bank?: string;
    file_type?: 'pdf' | 'csv' | 'other';
    account_id?: string;
};

type StatementParsingErrorProperties = {
    error_code: string;
    operation_id?: string;
    bank_choice?: string;
    file_type?: string;
};

type PdfMapColumnsOpenedProperties = {
    fileKey?: string;
    accountId?: string;
};

type ImportCompletedProperties = {
    import_id?: string;
    source?: 'csv' | 'pdf' | 'bank' | 'manual';
    count?: number;
    bank?: string;
    currency?: string;
    conversion_rate?: number;
    duration_ms?: number;
};

type FileUploadProperties = {
    file_type?: string;
    file_size?: number;
    context?: string;
    error_code?: string;
};

// Accounts & Bank Sync
type BankSyncProperties = { provider?: string; duration_ms?: number; error_code?: string, accounts_found?: number };
type AccountCreatedProperties = {
    account_type: string;
    account_subtype?: string;
    visibility?: string;
    is_first_account: boolean;
    userId?: string;
    timestamp?: string;
};

type AccountCreationAttemptProperties = {
    account_type: string;
    account_subtype?: string;
    visibility?: string;
};

// Insights & Recommandations
type InsightViewedProperties = {
    insight_type: string;
    insight_title?: string;
    value?: number;
    source: 'ai' | 'rule' | 'system';
};

type RecommendationProperties = {
    recommendation_id?: string;
    recommendation_type?: string;
    source?: 'ai' | 'manual' | 'rule';
    action?: 'clicked' | 'accepted' | 'dismissed';
};

type AiChatStartedProperties = { context?: string; source?: string };

// Expenses
type ExpenseLoggedProperties = { category?: string; amount?: number; currency?: string; source?: string };
type EmotionalSpendingLoggedProperties = { emotion: string; category?: string; amount?: number };

// Reports
type ReportViewedProperties = { report_type: string; filters?: Record<string, unknown> };

// Nav, Search, Tools
type TourProperties = { tour_name: string, step_index?: number };
type SearchPerformedProperties = { query: string; results_count?: number; context?: string };
type FilterAppliedProperties = { filter_name: string; context?: string };
type ToolUsedProperties = { tool_name: string; context?: string };

// Subscriptions
type CheckoutStartedProperties = { plan?: string; price?: number, billing_period?: string, payment_method?: string };
type CheckoutFailedProperties = { plan?: string; payment_method?: string; error_code?: string };
type SubscriptionProperties = { plan?: string; promo?: string };
type SubscriptionChangeFailedProperties = { plan?: string; error_code?: string };

// UI / UX
type ThemeToggledProperties = { theme: string };
type LanguageChangedProperties = { language: string };
type AppModeChangedProperties = { mode: 'basic' | 'full'; previous_mode?: 'basic' | 'full' };

// Errors
type ClientErrorProperties = { component?: string; severity?: 'warning' | 'error' | 'fatal' };

// Household
type HouseholdInviteProperties = { role?: string };

// Currency / FX (docs/currencies-unification)
type UnsupportedCurrencyProperties = { code: string; context: string };
type CurrencyMismatchProperties = { code: string; accountCurrency: string; context: string };

// CENTRALIZED EVENTS MAP

export type EventMap = {
    // Auth / Onboarding

    // Auth lifecycle
    signup_started: AuthProperties | undefined;
    signup_completed: AuthProperties | undefined;
    signup_failed: AuthProperties & FailedProperties | undefined;

    login_started: AuthProperties | undefined;
    login_completed: AuthProperties | undefined;
    login_failed: AuthProperties & FailedProperties | undefined;

    logout: undefined;

    onboarding_started: undefined;
    onboarding_step_completed: OnboardingStepProperties;
    onboarding_completed: undefined;

    // Transactions
    transaction_created: TransactionCreatedProperties;
    transaction_creation_failed: TransactionCreationFailedProperties;
    transaction_updated: TransactionUpdatedProperties;
    transaction_deleted: TransactionDeletedProperties;

    // Budgets, goals & debts
    budget_creation_started: BudgetCreatedProperties;
    budget_created: BudgetCreatedProperties;
    budget_creation_failed: BudgetCreationFailedProperties;
    savings_goal_creation_started: SavingsGoalCreatedProperties;
    savings_goal_created: SavingsGoalCreatedProperties;
    savings_goal_creation_failed: SavingsGoalCreatedProperties;
    debt_payment_recorded: DebtPaymentRecordedProperties;

    // Imports & statement parsing
    statement_parsing_started: StatementParsingStartedProperties;
    statement_parsed: StatementParsedProperties;
    statement_imported: ImportCompletedProperties;
    statement_parsing_failed: StatementParsingErrorProperties;
    pdf_map_columns_opened: PdfMapColumnsOpenedProperties;

    // File uploads
    file_upload_started: FileUploadProperties;
    file_upload_completed: FileUploadProperties;
    file_upload_failed: FileUploadProperties;

    // Categories
    category_added: { category_name: string; parent?: string };
    category_removed: { category_name: string };

    // Accounts & Bank Sync
    bank_sync_started: BankSyncProperties | undefined;
    bank_sync_completed: BankSyncProperties | undefined;
    bank_sync_failed: BankSyncProperties | undefined;

    account_creation_started: AccountCreationAttemptProperties;
    account_created: AccountCreatedProperties;
    account_creation_failed: AccountCreationAttemptProperties;

    // IA / Insights / recommandations
    insight_viewed: InsightViewedProperties;
    recommendation_action: RecommendationProperties;
    ai_chat_started: AiChatStartedProperties | undefined;

    // Expenses
    expense_logged: ExpenseLoggedProperties;
    emotional_spending_logged: EmotionalSpendingLoggedProperties;

    // Reports
    report_viewed: ReportViewedProperties;

    // Tours, search, filters, tools
    tour_started: TourProperties;
    tour_completed: TourProperties;
    tour_skipped: TourProperties;
    search_performed: SearchPerformedProperties;
    filter_applied: FilterAppliedProperties;
    tool_used: ToolUsedProperties;

    // Subscriptions / Payments
    pricing_page_viewed: { source?: string } | undefined;
    checkout_started: CheckoutStartedProperties | undefined;
    checkout_completed: { plan?: string } | undefined;
    checkout_failed: CheckoutFailedProperties | undefined;
    subscription_changed: SubscriptionProperties | undefined;
    subscription_change_failed: SubscriptionChangeFailedProperties | undefined;
    subscription_canceled: SubscriptionProperties | undefined;
    subscription_cancellation_failed: SubscriptionChangeFailedProperties | undefined;

    // UI / UX
    theme_toggled: ThemeToggledProperties;
    language_changed: LanguageChangedProperties;
    app_mode_changed: AppModeChangedProperties;

    // Errors
    client_error: ClientErrorProperties;

    // Household
    household_creation_started: undefined;
    household_created: undefined;
    household_creation_failed: undefined;
    household_invite_sent: HouseholdInviteProperties;
    household_invite_accepted: undefined;
    household_invite_rejected: undefined;

    // Privacy / Data Deletion
    data_deletion_requested: undefined;

    // Currency / FX (docs/currencies-unification)
    fx_unsupported_currency: UnsupportedCurrencyProperties;
    fx_currency_mismatch: CurrencyMismatchProperties;
};

// SDK HELPER & ANALYTICS API

/**
 * Checks if the PostHog SDK is ready and loaded.
 */
function isPostHogReady(): boolean {
    const ready =
        typeof window !== 'undefined' &&
        !!posthog &&
        typeof posthog.capture === 'function' &&
        !!(posthog as any).__loaded;

    if (!ready && getCookieConsent() === 'accepted') {
        console.warn('[Analytics] PostHog is not initialized yet.');
    }
    return ready;
}

export const analytics = {
    /**
     * Sends a typed event to PostHog.
     * Example: analytics.track('transaction_created', { transaction_type: 'expense', ... })
     */
    track<E extends keyof EventMap>(
        ...args: EventMap[E] extends undefined
            ? [event: E]
            : [event: E, properties: EventMap[E]]
    ) {
        if (!isPostHogReady()) return;
        const [event, properties] = args;
        try {
            posthog.capture(event as string, properties);
        } catch (error) {
            console.error(`[Analytics] Failed to track "${String(event)}"`, error);
        }
    },

    /**
     * Identifies the current user with PostHog.
     */
    identify(userId: string, properties?: Record<string, unknown>) {
        if (!isPostHogReady()) return;
        try {
            posthog.identify(userId, properties);
        } catch (error) {
            console.error('[Analytics] Failed to identify user', error);
        }
    },

    /**
     * Sets the properties of the current user.
     * Use `forFlagsOnly: true` for targeting by Feature Flag on an anonymous profile
     * without creating a person profile prematurely.
     */
    setUserProperties(
        userProperties: Record<string, unknown>,
        options: { forFlagsOnly?: boolean } = {}
    ) {
        if (!isPostHogReady()) return;
        try {
            if (options.forFlagsOnly) {
                posthog.setPersonPropertiesForFlags(userProperties);
            } else {
                posthog.setPersonProperties(userProperties);
            }
        } catch (error) {
            console.error('[Analytics] Failed to set user properties', error);
        }
    },

    /**
     * Resets the PostHog session (removes the identity and generates a new anonymous ID).
     */
    reset() {
        if (!isPostHogReady()) return;
        try {
            posthog.reset();
        } catch (error) {
            console.error('[Analytics] Failed to reset user', error);
        }
    },

    /**
     * Complete logout: captures the logout event and resets the session.
     */
    logout() {
        this.track('logout');
        this.reset();
    },
};
